import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {NOTIFICATION_VARIANTS} from '@nur-portal/ui-kit';

import {addJitsuProperty} from '../../../../../../services/analytics/jitsu-helpers';
import hiveSDK from '../../../../../../services/hive-sdk';
import {getAccessToken} from '../../../../../../services/tokens';
import {showNotification} from '../../../../../../website-ui/components/notification-center/helpers';
import {OTP_CODE_ALREADY_EXISTS} from '../../../../../../website-ui/constants/errors';
import {PhoneNumberForm} from '../../../../../../website-ui/forms/phone-number';
import {SMSCodeForm} from '../../../../../../website-ui/forms/sms-code';
import {isCommonError} from '../../../../../../website-ui/helpers/common-error';
import {DEFAULT_CODE_TTL, unmaskPhoneNumber} from '../../../../../../website-ui/helpers/phone-number';
import {refreshUserToken} from '../../../../../../website-ui/helpers/refresh-user-token';

const PHONE_STEPS = {
  PHONE: 0,
  SMS: 1
};

const StepPhone = ({
  goNext = () => {},
  onPhoneSubmitClick = () => {},
  onPhoneCancelClick = () => {},
  onSMSCodeSubmitClick = () => {},
  onPhoneChangeSuccess = () => {},
  onPhoneSubmitError = () => {}
}) => {
  const {t} = useTranslation();
  const [currentStep, setCurrentStep] = useState(PHONE_STEPS.PHONE);

  const [phoneError, setPhoneError] = useState();
  const [codeError, setCodeError] = useState();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [transactionId, setTransactionId] = useState(null);
  const [SMSCodeTTL, setSMSCodeTTL] = useState(0);

  const handlePhoneResponse = ({
    responseTransactionId,
    responseTTL,
    responsePhone
  }) => {
    setTransactionId(responseTransactionId);
    setSMSCodeTTL(responseTTL || DEFAULT_CODE_TTL);
    if (responsePhone) {
      setPhoneNumber(responsePhone);
    }
  };

  const phoneSubmitCallback = async ({phone}) => {
    await refreshUserToken(false);
    await hiveSDK.replacePhoneNumber(
      {phone: unmaskPhoneNumber(phone)},
      response => {
        handlePhoneResponse({
          responseTransactionId: response.transactionId,
          responseTTL: response.ttl,
          responsePhone: phone
        });
        setCurrentStep(PHONE_STEPS.SMS);
      },
      error => {
        onPhoneSubmitError(error);
        if (error.code === OTP_CODE_ALREADY_EXISTS) {
          handlePhoneResponse({
            responseTransactionId: error?.data?.transactionId,
            responseTTL: error?.data?.ttl,
            responsePhone: phone
          });
          setCurrentStep(PHONE_STEPS.SMS);
          return;
        }
        if (isCommonError(error.code)) {
          showNotification({
            variant: NOTIFICATION_VARIANTS.ALERT,
            text: t(`errors:common.${error.code}`)
          });
        } else {
          setPhoneError(error);
        }
      },
      getAccessToken(),
    );
  };

  const codeSubmitCallback = async ({code}) => {
    await hiveSDK.phoneNumberComplete(
      {
        phone: unmaskPhoneNumber(phoneNumber),
        code,
        transactionId
      },
      () => {
        showNotification({
          variant: NOTIFICATION_VARIANTS.SUCCESS,
          text: t('forms:phoneNumber.notifications.success')
        });
        onPhoneChangeSuccess();
        addJitsuProperty('user_has_phone_linked', true);
        refreshUserToken();
        if (goNext) {
          goNext();
        }
      },
      error => {
        if (isCommonError(error.code)) {
          showNotification({
            variant: NOTIFICATION_VARIANTS.ALERT,
            text: t(`errors:common.${error.code}`)
          });
        } else {
          setCodeError(error);
        }
      },
      getAccessToken(),
      false
    );
  };

  const handleResendCode = async () => {
    if (SMSCodeTTL) {
      return;
    }

    await refreshUserToken(false);
    await hiveSDK.replacePhoneNumber(
      {phone: unmaskPhoneNumber(phoneNumber)},
      response => {
        handlePhoneResponse({
          responseTransactionId: response.transactionId,
          responseTTL: response.ttl
        });
        showNotification({
          variant: NOTIFICATION_VARIANTS.INFO,
          text: t('forms:SMSCode.notifications.success')
        });
      },
      () => {},
      getAccessToken(),
    );
  };

  return (
    <>
      {currentStep === PHONE_STEPS.PHONE &&
        <PhoneNumberForm
          initialPhoneNumber={phoneNumber || ''}
          formSubmitCallback={phoneSubmitCallback}
          responseError={phoneError}
          onCancelClick={onPhoneCancelClick}
          onSubmitClick={onPhoneSubmitClick}
        />
      }
      {currentStep === PHONE_STEPS.SMS &&
        <SMSCodeForm
          phoneNumber={phoneNumber}
          SMSCodeTTL={SMSCodeTTL}
          responseError={codeError}
          formSubmitCallback={codeSubmitCallback}
          onCancelClick={() => setCurrentStep(PHONE_STEPS.PHONE)}
          onSubmitClick={onSMSCodeSubmitClick}
          onResendSMSCode={handleResendCode}
        />
      }
    </>
  );
};

export {StepPhone};
